import { useEffect, useState } from 'react';
import { formatLocation } from "../../components/helperFunctions";

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Card } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import ReservationsDialog from '../../components/reservations-dialog';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export default function ItemTable({
    InventoryItem,
    totalQuantity,
    reservations,
    refreshItemData
}) {
    const [toDisplayItem, setToDisplayItem] = useState(null);
    const [totalReservedQty, setTotalReservedQty] = useState(0);
    const [openDialog, setOpenDialog] = useState(false);

    useEffect(() => {
        if (InventoryItem.locationId) {
            setToDisplayItem({
                ...InventoryItem,
                city: InventoryItem.locationId.city,
                locationKey: formatLocation(InventoryItem, InventoryItem.locationId)
            });
        } else {
            setToDisplayItem({
                ...InventoryItem,
                locationKey: formatLocation(InventoryItem)
            });
        }
        calculateTotalReservedQty();
    }, [InventoryItem, reservations]);

    const calculateTotalReservedQty = () => {
        let total = 0;
        reservations.forEach(reservation => {
            if (reservation?.status === 'allocated') {
                total += reservation.qty;
            }
        })
        setTotalReservedQty(total);
    }

    // Style for the table row's bottm border 
    const borderBottom = {
        borderBottom: ({ borders: { borderWidth }, palette: { light } }) =>
            `${borderWidth[1]} solid ${light.main}`,
    };
    // Only render the table if the InventoryItem is not null
    if (InventoryItem) {
        return toDisplayItem && (
            <Card>

                {/* Reservations Dialog */}
                <ReservationsDialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    reservations={reservations}
                    refreshItemData={refreshItemData}
                    setTotalReservedQty={setTotalReservedQty}
                />

                <MDBox >

                    {/* Inventory Item Data */}
                    <MDBox p={2}>
                        <MDBox width="100%" overflow="auto">
                            <Table sx={{ minWidth: "100%" }}>
                                <MDBox component="thead">
                                    <TableRow>
                                        <MDBox
                                            component="th"
                                            width={{ xs: "50%", md: "75%" }}
                                            py={1.5} px={1} textAlign="left"
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="h6" color="text" fontWeight="medium">
                                                Attributes
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox
                                            component="th"
                                            py={1.5} pl={3} pr={1}
                                            textAlign="left"
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="h6" color="text" fontWeight="medium">
                                                Values
                                            </MDTypography>
                                        </MDBox>

                                    </TableRow>
                                </MDBox>
                                <TableBody>

                                    {/* City */}
                                    <TableRow >
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                Warehouse
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                {toDisplayItem.city}
                                            </MDTypography>
                                        </MDBox>
                                    </TableRow>

                                    {/* Location */}
                                    <TableRow >
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                Location
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                {toDisplayItem.locationKey}
                                            </MDTypography>
                                        </MDBox>
                                    </TableRow>

                                    {/* Quantity Available In Location */}
                                    <TableRow >
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDBox display="flex" alignItems="center" columnGap={0.5}>
                                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                                    Qty Available in Loc.
                                                </MDTypography>
                                                <Tooltip title="Available quantity in this location, excluding the reserved qty in this location">
                                                    <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                                                </Tooltip>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                {toDisplayItem.quantityIn}
                                            </MDTypography>
                                        </MDBox>
                                    </TableRow>

                                    {/* Total Available Qty */}
                                    <TableRow >
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDBox display="flex" alignItems="center" columnGap={0.5}>
                                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                                    Total Available Qty
                                                </MDTypography>
                                                <Tooltip title="Available quantity across all locations, excluding all reserved quantities">
                                                    <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                                                </Tooltip>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                {totalQuantity}
                                            </MDTypography>
                                        </MDBox>
                                    </TableRow>

                                    <TableRow >
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                Price Per Unit
                                            </MDTypography>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDTypography variant="body2" color="text" fontWeight="regular">
                                                {toDisplayItem.pricePerUnit ? "C$ " + toDisplayItem.pricePerUnit : "No Price Provided"}
                                            </MDTypography>
                                        </MDBox>
                                    </TableRow>
                                    {/* Qty Reserved in Location */}
                                    <TableRow>
                                        <MDBox component="td" textAlign="left" p={1} sx={borderBottom}>
                                            <MDBox display="flex" alignItems="center" columnGap={0.5}>
                                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                                    Qty Reserved in Loc.
                                                </MDTypography>
                                                <Tooltip title="Reserved quantity in this location, click the arrow to view reservation details">
                                                    <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
                                                </Tooltip>
                                            </MDBox>
                                        </MDBox>
                                        <MDBox
                                            component="td"
                                            textAlign="left"
                                            py={1} pr={1} pl={3}
                                            sx={borderBottom}
                                        >
                                            <MDBox display="flex" alignItems="center" columnGap={0.5}>
                                                <MDTypography variant="body2" color="text" fontWeight="regular">
                                                    {totalReservedQty ? totalReservedQty : "None"}
                                                </MDTypography>
                                                {totalReservedQty > 0 && (
                                                    <Tooltip title="View Reservations">
                                                        <IconButton
                                                            onClick={() => setOpenDialog(true)}
                                                        >
                                                            <ArrowForwardIosIcon sx={{ fontSize: "1rem" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </MDBox>
                                        </MDBox>
                                    </TableRow>

                                </TableBody>
                            </Table>
                        </MDBox>
                    </MDBox>

                </MDBox>
            </Card>
        )
    }
}